/**
 * Reusable bulk salary-batch panel. Embedded in:
 *   • HR > Salary Requests tab        (mode="hr")        — create + view all
 *   • Finance > Payroll tab           (mode="finance")    — review & clear
 *   • Approvals page (HR Payroll)     (mode="approvals")  — CEO final approval
 *
 * Backed by /api/hr/salary-batches (existing router). Status flow:
 *   draft → submitted → under_finance_review → finance_cleared / partially_cleared / rejected_by_finance
 *        → pending_final_approval → approved / partially_approved / rejected
 *        → paid_disbursed → closed
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDateTime } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/app/ui-bits";
import {
  Plus, CurrencyDollar, Eye, CheckCircle, XCircle,
  ArrowCounterClockwise, Money,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";

export const SALARY_BATCH_STATUS_LABELS = {
  draft: "Draft",
  submitted: "Submitted",
  under_finance_review: "Under finance review",
  finance_cleared: "Finance cleared",
  partially_cleared: "Partially cleared",
  rejected_by_finance: "Rejected by finance",
  pending_final_approval: "Pending final approval",
  approved: "Approved",
  partially_approved: "Partially approved",
  rejected: "Rejected",
  paid_disbursed: "Paid · disbursed",
  closed: "Closed",
};

const fmtUSD = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

// Status sets per mode
const MODE_TABS = {
  hr: [
    ["all", "All"],
    ["draft", "Draft"],
    ["submitted", "Submitted"],
    ["under_finance_review", "With Finance"],
    ["pending_final_approval", "With CEO"],
    ["paid_disbursed", "Paid"],
  ],
  finance: [
    ["under_finance_review", "Awaiting review"],
    ["partially_cleared", "Partially cleared"],
    ["finance_cleared", "Cleared · with CEO"],
    ["approved", "Approved · pay"],
    ["paid_disbursed", "Paid"],
    ["all", "All"],
  ],
  approvals: [
    ["finance_cleared", "Awaiting CEO"],
    ["partially_cleared", "Partially cleared"],
    ["approved", "Approved"],
    ["paid_disbursed", "Paid"],
    ["all", "All"],
  ],
};

function NewBatchDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    period_month: new Date().toISOString().slice(0, 7),
    notes: "",
    auto_submit: true,
    filters: {
      department: "", staff_type: "", payment_cycle: "", status: "active",
      contract_type: "", project_id: "",
    },
  });
  const setF = (k, v) => setForm((s) => ({ ...s, filters: { ...s.filters, [k]: v } }));

  const previewBatch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hr/salary-batches/_preview", form);
      setPreview(data);
      toast.success(`${data.count} staff matched · ${fmtUSD(data.total_usd)}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hr/salary-batches", form);
      toast.success(`Batch ${data.batch_ref} created and submitted to Finance`);
      onCreated?.(data);
      setOpen(false); setPreview(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-primary-blue" size="sm" data-testid="new-batch-btn">
          <Plus size={14} className="mr-2" /> New bulk salary batch
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">New bulk salary batch</DialogTitle>
          <DialogDescription>
            Pick a payroll period and filters. Each matched employee becomes a line on this batch. After submission the batch routes to Finance for clearance, then CEO for final approval.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label>Period (YYYY-MM) *</Label>
            <Input type="month" value={form.period_month}
              onChange={(e) => setForm({ ...form, period_month: e.target.value })}
              data-testid="batch-period" />
          </div>
          <div><Label>Department</Label>
            <Input placeholder="e.g. Engineering"
              value={form.filters.department} onChange={(e) => setF("department", e.target.value)} />
          </div>
          <div><Label>Staff type</Label>
            <Select value={form.filters.staff_type || "_any"}
              onValueChange={(v) => setF("staff_type", v === "_any" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_any">Any</SelectItem>
                <SelectItem value="Office">Office</SelectItem>
                <SelectItem value="Field">Field</SelectItem>
                <SelectItem value="Site">Site</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Payment cycle</Label>
            <Select value={form.filters.payment_cycle || "_any"}
              onValueChange={(v) => setF("payment_cycle", v === "_any" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_any">Any</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="biweekly">Biweekly</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Contract type</Label>
            <Select value={form.filters.contract_type || "_any"}
              onValueChange={(v) => setF("contract_type", v === "_any" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_any">Any</SelectItem>
                <SelectItem value="permanent">Permanent</SelectItem>
                <SelectItem value="fixed_term">Fixed term</SelectItem>
                <SelectItem value="project_based">Project-based</SelectItem>
                <SelectItem value="consultancy">Consultancy</SelectItem>
                <SelectItem value="probation">Probation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>Notes</Label>
            <Textarea rows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        {preview && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm">
            <div className="font-semibold text-blue-900">
              {preview.count} staff matched · Total {fmtUSD(preview.total_usd)}
            </div>
            <div className="mt-1.5 max-h-32 overflow-y-auto text-xs text-blue-900">
              {preview.staff.slice(0, 6).map((s) => (
                <div key={s.id}>· {s.full_name} — {fmtUSD(s.projected_amount)}</div>
              ))}
              {preview.staff.length > 6 && <div className="text-blue-600">…and {preview.staff.length - 6} more</div>}
            </div>
          </div>
        )}
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={previewBatch} disabled={busy} data-testid="preview-batch">Preview</Button>
          <Button onClick={submit} disabled={busy || !preview} className="btn-primary-blue" data-testid="create-batch">
            {busy ? "Creating…" : "Create + submit to Finance"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BatchDetail({ batch, refresh, mode }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const isFinance =
    hasMinRole(user, "manager") &&
    (user.role === "admin" || user.role === "super_admin"
     || user.dept_scope === "finance" || !user.dept_scope);
  const isAdmin = hasMinRole(user, "admin");

  const toggle = (id) =>
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const review = async (decision) => {
    setBusy(true);
    try {
      const payload = { decision, batch_comment: comment, line_comment: "" };
      if (decision.endsWith("_selected")) {
        if (selected.size === 0) { toast.error("Select at least one line"); setBusy(false); return; }
        payload.line_ids = [...selected];
      }
      await api.post(`/hr/salary-batches/${batch.id}/finance-review`, payload);
      toast.success("Review submitted");
      refresh();
      setComment(""); setSelected(new Set());
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const finalApprove = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/hr/salary-batches/${batch.id}/final-approval`, { decision, comment });
      toast.success(decision === "approve" ? "Batch approved by CEO"
        : decision === "reject" ? "Batch rejected"
        : "Returned to Finance");
      refresh(); setComment("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const markPaid = async () => {
    const ref = window.prompt("Payment reference (optional):", "");
    if (ref === null) return;
    setBusy(true);
    try {
      await api.post(`/hr/salary-batches/${batch.id}/mark-paid`, { payment_reference: ref || "", comment });
      toast.success("Marked paid"); refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const inFinanceReview = ["under_finance_review", "partially_cleared"].includes(batch.status);
  const pendingCeo = ["finance_cleared", "partially_cleared"].includes(batch.status);
  const payableNow = ["approved", "partially_approved"].includes(batch.status);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`view-${batch.id}`}>
          <Eye size={12} className="mr-1" /> Open
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex flex-wrap items-center gap-2">
            <span>{batch.batch_ref}</span>
            <span className="font-mono-alt text-xs text-slate-500">· {batch.period_month}</span>
            <StatusBadge status={batch.status} />
          </DialogTitle>
          <DialogDescription>
            {batch.employee_count} employees · {fmtUSD(batch.total_amount)} · created by {batch.created_by_name}
            {batch.notes ? <span className="ml-2 italic">“{batch.notes}”</span> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <AuditTrailDrawer entity="hr_salary_batch" entityId={batch.id}
            label={`Salary batch ${batch.batch_ref}`} />
        </div>

        {/* Lines — desktop table */}
        <div className="hidden md:block overflow-x-auto rounded border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-left text-[10px] font-mono-alt uppercase tracking-wider text-slate-500">
              <tr>
                <th className="w-8 px-2 py-2">
                  <input
                    type="checkbox"
                    disabled={!inFinanceReview}
                    onChange={(e) =>
                      setSelected(e.target.checked ? new Set(batch.lines.map((l) => l.id)) : new Set())
                    }
                    checked={inFinanceReview && selected.size === batch.lines.length && batch.lines.length > 0}
                    data-testid="select-all-lines"
                  />
                </th>
                <th className="px-2 py-2">Employee</th>
                <th className="px-2 py-2">Department</th>
                <th className="px-2 py-2">Designation</th>
                <th className="px-2 py-2 text-right">Net amount</th>
                <th className="px-2 py-2">Line status</th>
                <th className="px-2 py-2">Finance comment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {batch.lines.map((line) => (
                <tr key={line.id} data-testid={`line-${line.id}`}>
                  <td className="px-2 py-1.5">
                    <input type="checkbox" checked={selected.has(line.id)} onChange={() => toggle(line.id)}
                      disabled={!inFinanceReview}
                      data-testid={`line-cb-${line.id}`} />
                  </td>
                  <td className="px-2 py-1.5 font-semibold">{line.staff_name}</td>
                  <td className="px-2 py-1.5">{line.department || "—"}</td>
                  <td className="px-2 py-1.5">{line.designation || "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmtUSD(line.net_amount)}</td>
                  <td className="px-2 py-1.5">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-mono-alt uppercase ${{
                      pending: "bg-slate-100 text-slate-700",
                      cleared: "bg-emerald-100 text-emerald-700",
                      rejected: "bg-red-100 text-red-700",
                      paid: "bg-emerald-200 text-emerald-900",
                    }[line.line_status] || "bg-slate-100"}`}>{line.line_status}</span>
                  </td>
                  <td className="px-2 py-1.5 text-slate-600">{line.finance_comment || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Lines — mobile stacked cards */}
        <ul className="md:hidden divide-y divide-slate-100 rounded border border-slate-200">
          {batch.lines.map((line) => (
            <li key={line.id} className="p-3 text-sm" data-testid={`mline-${line.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold">{line.staff_name}</div>
                  <div className="text-xs text-slate-500">{line.department || "—"} · {line.designation || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-alt font-semibold tabular-nums">{fmtUSD(line.net_amount)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{line.line_status}</div>
                </div>
              </div>
              {inFinanceReview && (
                <label className="mt-2 flex items-center gap-2 text-xs text-slate-700">
                  <input type="checkbox" checked={selected.has(line.id)} onChange={() => toggle(line.id)}
                    data-testid={`mline-cb-${line.id}`} />
                  Select for action
                </label>
              )}
              {line.finance_comment && (
                <div className="mt-1.5 text-[11px] text-slate-600">Finance: {line.finance_comment}</div>
              )}
            </li>
          ))}
        </ul>

        {/* Action bar */}
        <div className="mt-3">
          <Label>Comment for this action</Label>
          <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder="Reason / context for the decision…" data-testid="action-comment" />
        </div>

        <DialogFooter className="flex flex-wrap gap-2 justify-end">
          {inFinanceReview && isFinance && mode !== "approvals" ? (
            <>
              <Button variant="outline" onClick={() => review("approve_selected")} disabled={busy} data-testid="approve-selected">
                Approve selected ({selected.size})
              </Button>
              <Button variant="outline" onClick={() => review("reject_selected")} disabled={busy} data-testid="reject-selected">
                Reject selected ({selected.size})
              </Button>
              <Button onClick={() => review("approve_all")} disabled={busy} className="btn-primary-blue" data-testid="approve-all">
                <CheckCircle size={14} className="mr-1.5" /> Approve all
              </Button>
              <Button onClick={() => review("reject_all")} disabled={busy} variant="destructive" data-testid="reject-all">
                <XCircle size={14} className="mr-1.5" /> Reject all
              </Button>
            </>
          ) : pendingCeo && isAdmin ? (
            <>
              <Button onClick={() => finalApprove("return")} variant="outline" disabled={busy} data-testid="final-return">
                <ArrowCounterClockwise size={14} className="mr-1.5" /> Return to Finance
              </Button>
              <Button onClick={() => finalApprove("reject")} variant="destructive" disabled={busy} data-testid="final-reject">
                <XCircle size={14} className="mr-1.5" /> Reject
              </Button>
              <Button onClick={() => finalApprove("approve")} className="btn-primary-blue" disabled={busy} data-testid="final-approve">
                <CheckCircle size={14} className="mr-1.5" /> Final approve
              </Button>
            </>
          ) : payableNow && isFinance ? (
            <Button onClick={markPaid} className="btn-primary-blue" disabled={busy} data-testid="mark-paid">
              <Money size={14} className="mr-1.5" /> Mark paid / disbursed
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SalaryBatchesPanel({
  mode = "hr",     // "hr" | "finance" | "approvals"
  title,
  description,
}) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const tabs = MODE_TABS[mode] || MODE_TABS.hr;
  const [tab, setTab] = useState(tabs[0][0]);

  const load = async () => {
    setLoading(true);
    try {
      const params = tab === "all" ? "" : `?status=${tab}`;
      const { data } = await api.get(`/hr/salary-batches${params}`);
      setItems(data.items || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const canCreate = mode === "hr" && hasMinRole(user, "manager");

  return (
    <div data-testid={`salary-batches-panel-${mode}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          {title && <h3 className="font-display text-base font-semibold text-slate-900">{title}</h3>}
          {description && <p className="text-xs text-slate-500">{description}</p>}
        </div>
        {canCreate && <NewBatchDialog onCreated={load} />}
      </div>

      {/* Status tabs — horizontal scroll on mobile */}
      <div className="mt-3 -mx-1 overflow-x-auto pb-1">
        <div className="inline-flex gap-0.5 rounded-md border border-slate-200 bg-white p-0.5">
          {tabs.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`bt-${mode}-${k}`}
              className={`whitespace-nowrap px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition ${
                tab === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="mt-4 font-mono-alt text-xs uppercase tracking-[0.25em] text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-4">
          <EmptyState
            icon={CurrencyDollar}
            title="No salary batches in this state"
            description={
              mode === "hr"
                ? "Click 'New bulk salary batch' to build one for this month's payroll."
                : mode === "finance"
                ? "There are no batches awaiting your review right now."
                : "There are no batches awaiting CEO approval right now."
            }
          />
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="mt-4 hidden md:block overflow-x-auto rounded border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-3 py-2">Batch ref</th>
                  <th className="px-3 py-2">Period</th>
                  <th className="px-3 py-2 text-right"># Staff</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2">Created by</th>
                  <th className="px-3 py-2">Submitted</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((b) => (
                  <tr key={b.id} data-testid={`batch-row-${b.id}`}>
                    <td className="px-3 py-2 font-mono-alt font-bold text-slate-900">{b.batch_ref}</td>
                    <td className="px-3 py-2 font-mono-alt text-xs">{b.period_month}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{b.employee_count}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtUSD(b.total_amount)}</td>
                    <td className="px-3 py-2 text-xs">{b.created_by_name || b.created_by_email}</td>
                    <td className="px-3 py-2 font-mono-alt text-xs">
                      {b.submission_date ? formatDateTime(b.submission_date) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={b.status} />
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {SALARY_BATCH_STATUS_LABELS[b.status] || b.status}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <BatchDetail batch={b} refresh={load} mode={mode} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="mt-4 grid gap-2 md:hidden">
            {items.map((b) => (
              <li key={b.id} className="rounded border border-slate-200 bg-white p-3" data-testid={`mbatch-${b.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono-alt text-sm font-bold text-slate-900">{b.batch_ref}</div>
                    <div className="text-xs text-slate-500">
                      {b.period_month} · {b.employee_count} staff · {fmtUSD(b.total_amount)}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      By {b.created_by_name || b.created_by_email}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <StatusBadge status={b.status} />
                    <div className="mt-1 text-[10px] text-slate-500">
                      {SALARY_BATCH_STATUS_LABELS[b.status] || b.status}
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex justify-end">
                  <BatchDetail batch={b} refresh={load} mode={mode} />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
